import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Gift, Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { creditQueries, redeemPromoCode } from "@/api/credits";
import { toast } from "@/hooks/use-toast";

const redeemFormSchema = z.object({
  promoCode: z.string().min(1, "Promo code is required"),
});

type RedeemValues = z.infer<typeof redeemFormSchema>;

export function RedeemPromoCard({ personaId }: { personaId: string }) {
  const queryClient = useQueryClient();
  const redeemForm = useForm<RedeemValues>({
    resolver: zodResolver(redeemFormSchema),
    defaultValues: { promoCode: "" },
  });

  const redeemMutation = useMutation({
    mutationFn: (data: RedeemValues) =>
      redeemPromoCode({ promo_id: data.promoCode, agent_id: personaId }),
    onSuccess: () => {
      queryClient.invalidateQueries(creditQueries.agentBalance(personaId));
      toast({
        title: "Success",
        description: "Promo code has been successfully redeemed.",
        variant: "default",
      });
      redeemForm.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.response?.data?.detail || "Failed to redeem promo code",
        variant: "destructive",
      });
    },
  });

  const onRedeemSubmit = (values: RedeemValues) => {
    redeemMutation.mutate(values);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Redeem Promo Code
        </CardTitle>
        <CardDescription>
          Apply a promo code to receive bonus credits
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...redeemForm}>
          <form
            onSubmit={redeemForm.handleSubmit(onRedeemSubmit)}
            className="space-y-4"
          >
            <FormField
              control={redeemForm.control}
              name="promoCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Promo Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter promo code"
                      {...field}
                      disabled={redeemMutation.isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={redeemMutation.isPending}
            >
              {redeemMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redeeming...
                </>
              ) : (
                "Redeem Code"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
